import { IconButton } from 'components/button'
import { DownArrowIcon, LeftArrowIcon, RightArrowIcon, UpArrowIcon } from 'components/icons'
import moment from 'moment'
import { useState } from 'react'
import { useGetPreviousPolicies } from '../api/getPerviousPolicies'
import { useMediaQuery } from 'react-responsive'
import { DetailWrapper, EmptyRecord, OverflowScroll, StyledTable, InfoColumn, StyledRow, ButtonCellCSS, ExpendedCSS } from 'features/incidents/components/style'
import LinearProgress from '@mui/material/LinearProgress'
import { StyledCell } from 'features/incidents/components/SentinelOne/IncidentsTable'
import { ClaimRowProps, RowProps } from 'features/incidents/components/type'
import { Box } from 'components/elements'
import { useGetClaimsInPlan } from '../api/getClaimsInPlanHistory'
import PolicyTimeline from './ChangePolicy/PolicyTimeline'
import { TERMINOLOGY } from 'utils/enum'

type ExpandProps = {
  row: Record<string, any>
  open: boolean
  onExpand: () => void
  isDesktopOrLaptop?: boolean
}

const dateConverter = (startDate: string, timeEnd: string) => {
  const newStartDate = new Date(startDate)
  const newEndDate = new Date(timeEnd)
  const result = moment(newEndDate).diff(newStartDate, 'months')

  return result
}

const POLICY_TABLE_COLUMNS = [
  { id: 'carrierName', label: `${TERMINOLOGY.partner.singular} Name`, minWidth: 150 },
  { id: 'policyName', label: `${TERMINOLOGY.warranty.singular} Name`, minWidth: 225 },
  { id: 'duration', label: 'Total Active Duration', minWidth: 225 },
  { id: 'startDate', label: 'Activation Date', minWidth: 200 },
  { id: 'endDate', label: ' Original Expiry Date', minWidth: 200 },
  // { id: 'status', label: 'Policy Status', minWidth: 225 },
  // { id: 'Time', label: 'Status Update Timestamp', minWidth: 250 },
]

const PreviousRecord = () => {
  const { data: PolicyList, isLoading } = useGetPreviousPolicies()
  const [openId, setOpenId] = useState(-1)

  const doc: Document = document
  const body = doc.querySelector('body')
  const isDesktopOrLaptop = useMediaQuery({ minWidth: 991 })

  const previousPolicyList = PolicyList?.plans || []
  return (
    <>
      <Box css={{ ...OverflowScroll, marginTop: '1.5rem' }}>
        <StyledTable css={{ marginBottom: '1rem' }} className={`${!isDesktopOrLaptop && previousPolicyList?.length ? 'table--mobile' : 'responsive-mobile'} `}>
          {isDesktopOrLaptop && (
            <thead>
              <StyledRow>
                <StyledCell as="th"></StyledCell>
                {POLICY_TABLE_COLUMNS.map(column => (
                  <StyledCell key={column.id} as="th" className={`minWidth-${column.minWidth}`} css={{ lineHeight: 1.9 }}>
                    {column.label}
                  </StyledCell>
                ))}
              </StyledRow>
            </thead>
          )}
          <tbody>
            {isLoading ? (
              <th colSpan={9}>
                <LinearProgress sx={{ height: '6px' }} />
              </th>
            ) : (
              <>
                {previousPolicyList?.length ? (
                  previousPolicyList?.map((row: any, idx: number) => (
                    <Row
                      key={idx}
                      row={row}
                      open={openId === idx}
                      id={idx}
                      onExpand={() => {
                        setOpenId(prev => (prev === idx ? -1 : idx))
                        openId === -1 ? body?.classList.add('overflow--hidden') : body?.classList.remove('overflow--hidden')
                      }}
                      isDesktopOrLaptop={isDesktopOrLaptop}
                    />
                  ))
                ) : (
                  <EmptyRecord colSpan={7}>No Data</EmptyRecord>
                )}
              </>
            )}
          </tbody>
        </StyledTable>
      </Box>
    </>
  )
}

const Row = ({ row, open, onExpand, isDesktopOrLaptop }: RowProps) => (
  <>
    <StyledRow className={open ? 'expanded--bg-color' : undefined}>
      {isDesktopOrLaptop && (
        <StyledCell css={{ p: 0, pl: '10px' }}>
          <IconButton onClick={onExpand}>{open ? <UpArrowIcon /> : <DownArrowIcon />}</IconButton>
        </StyledCell>
      )}
      <StyledCell>
        {!isDesktopOrLaptop && (
          <Box as="h4" css={{ mb: 0 }}>
            {TERMINOLOGY.partner.singular} Name
          </Box>
        )}
        {row.companyName || '-'}
      </StyledCell>
      <StyledCell>
        {!isDesktopOrLaptop && (
          <Box as="h4" css={{ mb: 0 }}>
            {TERMINOLOGY.warranty.singular} Name
          </Box>
        )}
        {row.planName || '-'}
      </StyledCell>
      <StyledCell>
        {!isDesktopOrLaptop && (
          <Box as="h4" css={{ mb: 0 }}>
            Total Active Duration
          </Box>
        )}
        {` active for ${dateConverter(row.startDate, row.cancelledAt === null ? row.expiredAt : row.cancelledAt)} months`}
      </StyledCell>
      <StyledCell>
        {!isDesktopOrLaptop && (
          <Box as="h4" css={{ mb: 0 }}>
            Activation Date
          </Box>
        )}
        {moment(row.startDate).format('MMM Do YY') || '0'}
      </StyledCell>
      <StyledCell>
        {!isDesktopOrLaptop && (
          <Box as="h4" css={{ mb: 0 }}>
            Original Expiry Date
          </Box>
        )}
        {row.expiredAt ? moment(row.expiredAt).format('MMM Do YY') : '-'}
      </StyledCell>

      {!isDesktopOrLaptop && (
        <StyledCell css={{ p: 0 }}>
          <IconButton css={ButtonCellCSS} className="detail--view" onClick={onExpand}>
            <Box as="h4" css={{ mr: '1rem', color: '$primary' }}>
              View Details
            </Box>
            <RightArrowIcon />
          </IconButton>
        </StyledCell>
      )}
    </StyledRow>

    <StyledRow className={open ? 'expanded' : undefined} css={ExpendedCSS}>
      {open && (
        <StyledCell colSpan={20} css={{ p: 0 }}>
          <ExpandedContent row={row} open={open} onExpand={onExpand} isDesktopOrLaptop={isDesktopOrLaptop} />
        </StyledCell>
      )}
    </StyledRow>
  </>
)

const ExpandedContent = ({ row, onExpand, isDesktopOrLaptop }: ExpandProps) => {
  const { data: ClaimListInPlan, isLoading } = useGetClaimsInPlan({ planId: row?.userInsurancePlanId })
  const claimList = ClaimListInPlan?.data?.resolvedClaims || []
  return (
    <DetailWrapper className="detail-box">
      {!isDesktopOrLaptop && (
        <IconButton onClick={onExpand} className="back--button">
          <LeftArrowIcon />
          <Box as="p" css={{ margin: '0 0 0 1.5rem' }}>
            {TERMINOLOGY.warranty.plural} History
          </Box>
        </IconButton>
      )}
      <Box className="detail-container">
        <InfoColumn>
          <Box as="p" className="t-key">
            {TERMINOLOGY.partner.singular} Name
          </Box>
          <Box as="p" className={'t-value'}>
            {row.companyName}
          </Box>
        </InfoColumn>
        <InfoColumn>
          <Box as="p" className="t-key">
            {TERMINOLOGY.warranty.singular} Description
          </Box>
          <Box as="p" className={'t-value'}>
            {row.description}
          </Box>
        </InfoColumn>

        <Box as="h4" css={{ mb: '$5' }}>
          {TERMINOLOGY.warranty.singular} Timeline:
        </Box>
        <PolicyTimeline row={row} />
        <StyledTable css={{ marginBottom: '1rem' }}>
          {isDesktopOrLaptop && (
            <thead>
              <StyledRow>
                <StyledCell as="th" className="minWidth-200">
                  Claim Number
                </StyledCell>
                <StyledCell as="th" className="minWidth-200">
                  Amount Deducted
                </StyledCell>
                <StyledCell as="th" className="minWidth-200">
                  Claim Date
                </StyledCell>
              </StyledRow>
            </thead>
          )}
          <tbody>
            {isLoading ? (
              <th colSpan={3}>
                <LinearProgress sx={{ height: '6px' }} />
              </th>
            ) : (
              <>
                {claimList?.length ? (
                  claimList?.map((row: any, idx: number) => <ClaimRow key={idx} row={row} id={idx} isDesktopOrLaptop={isDesktopOrLaptop} />)
                ) : (
                  <EmptyRecord colSpan={3}>No Data</EmptyRecord>
                )}
              </>
            )}
          </tbody>
        </StyledTable>
      </Box>
    </DetailWrapper>
  )
}

const ClaimRow = ({ row, isDesktopOrLaptop }: ClaimRowProps) => (
  <>
    <StyledRow>
      <StyledCell>
        {!isDesktopOrLaptop && (
          <Box as="h4" css={{ mb: 0 }}>
            Claim Number
          </Box>
        )}
        {row.threatid || '-'}
      </StyledCell>
      <StyledCell>
        {!isDesktopOrLaptop && (
          <Box as="h4" css={{ mb: 0 }}>
            Amount Deducted
          </Box>
        )}
        {'$' + row.claimamount || '-'}
      </StyledCell>
      <StyledCell>
        {!isDesktopOrLaptop && (
          <Box as="h4" css={{ mb: 0 }}>
            Claim Date
          </Box>
        )}
        {moment(row.createdat).format('MMM Do YY') || '0'}
      </StyledCell>
    </StyledRow>
  </>
)

export default PreviousRecord
